import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSpring } from '@react-spring/web';
import { useTranslation } from 'react-i18next';

// import { log } from '@app/shared/utils/logger';
import { useGetCurrentUserProfileQuery } from '@app/store/slices/users/usersApi';
import { useGetCurrentUserProgressQuery } from '@app/store/slices/userProgress/userProgressApi';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
// import { parseDate } from '@app/shared/utils/birthday';

export const useCompatibilityCalculator = () => {
  const { t } = useTranslation();

  const {
    data: userProfile,
    isLoading: isUserProfileLoading,
    isError: isUserProfileError,
  } = useGetCurrentUserProfileQuery();
  const {
    data: userProgress,
    isLoading: isUserProgressLoading,
    isError: isUserProgressError,
  } = useGetCurrentUserProgressQuery();

  const [firstDate, setFirstDate] = useState<Date | undefined>(undefined);
  const [secondDate, setSecondDate] = useState<Date | undefined>(undefined);
  const [trigger, setTrigger] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);

  useDisplayError(isUserProfileError || isUserProgressError, t('errors.failed_to_load_user'));

  useEffect(() => {
    if (userProfile?.birthday) {
      setFirstDate(new Date(userProfile.birthday));
    }
  }, [userProfile]);

  const springConfig = useMemo(() => ({ tension: 40, friction: 30 }), []);

  const leftPandaSpring = useSpring({
    to: { transform: `translateX(${String(trigger ? 40 : 0)}%)` },
    config: springConfig,
  });

  const rightPandaSpring = useSpring({
    to: { transform: `translateX(${String(trigger ? -40 : 0)}%)` },
    config: springConfig,
  });

  const checkButtonSpring = useSpring({
    to: { opacity: trigger ? 0 : 1 },
  });

  const handleCheckButtonClick = useCallback(() => {
    setTrigger(true);
  }, []);

  useEffect(() => {
    if (trigger) {
      const timer = setTimeout(() => {
        setIsResultModalOpen(true);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [trigger]);

  const handleModalClose = useCallback(() => {
    setIsResultModalOpen(false);
    setTrigger(false);
  }, []);

  //TODO: use this to calc compatibility
  // const parsedDate = useMemo(() => parseDate(firstDate), [firstDate]);

  // void log({level:'error', parsedDate});

  return {
    leftPandaSpring,
    rightPandaSpring,
    checkButtonSpring,
    userProfile,
    userProgress,
    firstDate,
    secondDate,
    setFirstDate,
    setSecondDate,
    isResultModalOpen,
    isUserProfileLoading,
    isUserProgressLoading,
    handleCheckButtonClick,
    handleModalClose,
  };
};
